import { useEffect, useMemo, useRef, useState } from 'react';
import { Check, Eye, Layers, Play, Sparkles } from 'lucide-react';
import { playClick, playCorrect, playWrong } from '../../utils/sounds';
import { PrecisionStats } from './PrecisionUI';
import MatchFace from './MatchFace';
import { canFlipCard, cardsMatch, createMatchDeck, getMatchRules, matchCardName, summarizeMatch, type MatchAttempt } from './matchRules';
import type { PrecisionProps, PrecisionResult } from './types';
import { seededRandom } from './dailyMatch';

type Phase = 'preview' | 'play' | 'feedback' | 'scan' | 'done';

export default function PrecisionMatch({ mode, tier, matchSeed, onComplete, onFail }: PrecisionProps) {
  const rules = useMemo(() => getMatchRules(mode, tier), [mode, tier]);
  const [cards] = useState(() => createMatchDeck(rules.level, rules.pairs, matchSeed ? seededRandom(matchSeed) : Math.random));
  const [phase, setPhase] = useState<Phase>('preview');
  const [first, setFirst] = useState<number | null>(null);
  const [second, setSecond] = useState<number | null>(null);
  const [matched, setMatched] = useState<Set<number>>(new Set());
  const [attempts, setAttempts] = useState<MatchAttempt[]>([]);
  const [scans, setScans] = useState(0);
  const [remaining, setRemaining] = useState(rules.previewMs);
  const [pairRemaining, setPairRemaining] = useState(0);
  const [feedback, setFeedback] = useState('先看牌面，把相互对应的位置分组记住');
  const state = useRef({ phase: 'preview' as Phase, first: null as number | null, matched: new Set<number>(), attempts: [] as MatchAttempt[], seen: new Set<number>(), firstPristine: false, scans: 0, assisted: false });
  const previewStart = useRef(performance.now());
  const sessionStart = useRef(performance.now());
  const started = useRef(0);
  const pairDeadline = useRef(0);
  const scanDeadline = useRef(0);
  const finished = useRef(false);
  const nextTimer = useRef<ReturnType<typeof setTimeout>>();
  const finishRef = useRef<(success: boolean, reason?: string) => void>(() => {});
  const resolveRef = useRef<(index: number | null) => void>(() => {});
  const callbacks = useRef({ onComplete, onFail });
  callbacks.current = { onComplete, onFail };
  const stats = summarizeMatch(attempts, rules.pairs, scans);

  const transition = (next: Phase) => { state.current.phase = next; setPhase(next); };
  finishRef.current = (success, reason) => {
    if (finished.current) return;
    finished.current = true; clearTimeout(nextTimer.current); transition('done');
    const s = state.current;
    const result = summarizeMatch(s.attempts, rules.pairs, s.scans);
    const precision: PrecisionResult = {
      version: 1, tier: rules.level, score: result.score, accuracy: result.accuracy,
      responseMs: null, bestResponseMs: null, maxCombo: result.maxCombo, perfectCount: result.pristine,
      mistakes: result.mistakes, total: s.attempts.length, trials: [],
      durationSeconds: (performance.now() - sessionStart.current) / 1000, failureReason: reason,
      metrics: [{ label: '完成配对', value: `${result.matched}/${rules.pairs} 对` }, { label: '配对尝试', value: `${s.attempts.length}/${rules.maxAttempts} 次` }, { label: '首次命中', value: `${result.pristine} 对` }, { label: '重看辅助', value: `${s.scans} 次` }, { label: '第二张超时', value: `${s.attempts.filter(a => a.outcome === 'timeout').length} 次` }, { label: '配对用时', value: `${((performance.now() - started.current) / 1000).toFixed(1)} 秒` }],
      insights: [result.mistakes ? '先记住一对的位置再翻牌；复盘中可逐次查看错配的两个位置与正确搭档。' : '位置提取很稳定。继续保持先回忆、后翻牌的节奏，不必追求抢快。', '准确率为成功配对占全部尝试的比例，第二张超时也计一次尝试。首次命中要求两张牌均未在配对阶段翻开，且未使用重看。'],
      matchReplay: { cards, complementary: rules.complementary, attempts: s.attempts },
    };
    const replay = { mechanic: 'match', precision };
    success ? callbacks.current.onComplete(result.score, replay) : callbacks.current.onFail(replay);
  };

  const begin = () => {
    if (state.current.phase !== 'preview' || finished.current) return;
    started.current = performance.now(); setRemaining(rules.timeMs); transition('play');
    setFeedback(rules.complementary ? '同形状、同标记，一实心一空心才是一对' : '找出形状、填充与标记完全相同的两张牌');
  };

  resolveRef.current = index => {
    const s = state.current;
    if (finished.current || s.phase !== 'play' || s.first === null) return;
    const now = performance.now();
    if (now >= started.current + rules.timeMs) { finishRef.current(false, '本局时间已用完。先在标准模式练习分组记忆，再挑战更多牌面。'); return; }
    const timedOut = index === null || (rules.secondCardMs > 0 && now >= pairDeadline.current);
    const a = s.first;
    const success = !timedOut && cardsMatch(cards[a], cards[index!], rules.complementary);
    const pristine = success && s.firstPristine && !s.seen.has(index!) && !s.assisted;
    if (!timedOut) s.seen.add(index!);
    const record: MatchAttempt = { first: a, second: timedOut ? null : index, outcome: timedOut ? 'timeout' : success ? 'match' : 'mismatch', pristine, timestamp: Math.round(now - started.current) };
    s.attempts = [...s.attempts, record];
    setAttempts(s.attempts); setSecond(timedOut ? null : index); transition('feedback');
    if (success) { s.matched = new Set([...s.matched, a, index!]); setMatched(new Set(s.matched)); playCorrect(); }
    else playWrong();
    setFeedback(timedOut ? '第二张选择超时 · 记一次失误' : success ? pristine ? '首次命中 · 记忆提取准确' : '配对成功 · 继续保持' : rules.complementary ? '不对应：形状和标记相同，填充必须一实一空' : '这两张不相同，记住位置再试');
    const summary = summarizeMatch(s.attempts, rules.pairs, s.scans);
    const completed = summary.matched === rules.pairs;
    const failure = summary.mistakes >= rules.mistakeLimit ? `累计 ${rules.mistakeLimit} 次错配或第二张超时。先回忆已翻开的位置，减少试探。` : s.attempts.length >= rules.maxAttempts ? `已用完 ${rules.maxAttempts} 次配对机会。对照复盘找出重复翻错的位置。` : undefined;
    nextTimer.current = setTimeout(() => {
      if (finished.current) return;
      if (completed) { finishRef.current(true); return; }
      if (failure) { finishRef.current(false, failure); return; }
      if (performance.now() >= started.current + rules.timeMs) { finishRef.current(false, '本局时间已用完。'); return; }
      s.first = null; pairDeadline.current = 0;
      setFirst(null); setSecond(null); setPairRemaining(0); transition('play');
    }, success ? 450 : 1050);
  };

  const flip = (index: number) => {
    const s = state.current;
    if (finished.current || !canFlipCard(index, cards.length, s.first, s.matched, s.phase !== 'play')) return;
    const now = performance.now();
    if (now >= started.current + rules.timeMs) { finishRef.current(false, '本局时间已用完，本次翻牌不计入。'); return; }
    if (s.first !== null) { resolveRef.current(index); return; }
    s.firstPristine = !s.seen.has(index); s.seen.add(index); s.first = index;
    pairDeadline.current = rules.secondCardMs ? now + rules.secondCardMs : 0;
    setFirst(index); setPairRemaining(rules.secondCardMs); playClick();
    setFeedback('第一张已翻开，回忆它的搭档在哪里');
  };
  const scan = () => {
    const s = state.current;
    if (finished.current || s.phase !== 'play' || s.first !== null || s.scans >= rules.scans) return;
    if (performance.now() >= started.current + rules.timeMs) { finishRef.current(false, '本局时间已用完。'); return; }
    s.scans++; s.assisted = true; setScans(s.scans); scanDeadline.current = performance.now() + rules.scanMs;
    transition('scan'); setFeedback('重看 1.5 秒，计时继续 · 扣 5 分，之后不再获首次命中奖励');
  };

  useEffect(() => {
    finished.current = false;
    const timer = setInterval(() => {
      const now = performance.now(), s = state.current;
      if (s.phase === 'preview') {
        setRemaining(Math.max(0, rules.previewMs - (now - previewStart.current)));
        if (now - previewStart.current >= rules.previewMs) begin();
      } else if (s.phase !== 'done') {
        const left = Math.max(0, rules.timeMs - (now - started.current)); setRemaining(left);
        // A final valid pair is settled once, even when its feedback crosses the deadline.
        if (!left && s.phase !== 'feedback') { finishRef.current(false, '本局时间已用完。'); return; }
        if (s.phase === 'play' && s.first !== null && rules.secondCardMs) {
          setPairRemaining(Math.max(0, pairDeadline.current - now));
          if (now >= pairDeadline.current) resolveRef.current(null);
        }
        if (s.phase === 'scan' && now >= scanDeadline.current) { transition('play'); setFeedback('辅助结束，先回忆再翻牌'); }
      }
    }, 40);
    return () => { finished.current = true; clearInterval(timer); clearTimeout(nextTimer.current); };
  }, [rules]);

  const last = attempts[attempts.length - 1];
  return <div className="precision-game match-game">
    <div className="match-header"><span><Layers size={15} />配对记忆实验</span><b>{(remaining / 1000).toFixed(1)}<small> 秒</small></b></div>
    <div className="precision-progress" role="progressbar" aria-label={phase === 'preview' ? '观察剩余时间' : '配对剩余时间'} aria-valuenow={Math.round(remaining / (phase === 'preview' ? rules.previewMs : rules.timeMs) * 100)} aria-valuemin={0} aria-valuemax={100}><div style={{ width: `${remaining / (phase === 'preview' ? rules.previewMs : rules.timeMs) * 100}%` }} /></div>
    <div className="match-status"><span>已配对 <b>{stats.matched}/{rules.pairs}</b></span><span>连击 <b>{stats.combo}</b></span><span>剩余容错 <b>{Math.max(0, rules.mistakeLimit - stats.mistakes)}</b></span></div>
    <div className="precision-rule match-rule"><span>{phase === 'preview' ? '观察位置 · 先记住再翻' : phase === 'scan' ? '辅助重看 · 计时继续' : '回忆位置 · 准确优先'}</span><strong>{rules.complementary ? '同形同标记，一实配一空' : rules.similarFaces ? '同形还要同填充' : '找到完全相同的一对'}</strong><small>{phase === 'preview' ? `${rules.pairs * 2} 张牌 · 位置全程固定` : first !== null && phase === 'play' && rules.secondCardMs ? `选择第二张 · 剩余 ${(pairRemaining / 1000).toFixed(1)} 秒` : `已用 ${attempts.length}/${rules.maxAttempts} 次配对机会`}</small></div>
    {rules.similarFaces && <div className="match-rule-demo"><MatchFace card={{ id: -1, pair: 0, shape: 0, fill: true, marked: false }} /><span>{rules.complementary ? '配对' : '不配对'}</span><MatchFace card={{ id: -2, pair: 0, shape: 0, fill: false, marked: false }} />{rules.complementary && <small>右上角标记也需一致</small>}</div>}
    <div className="match-board" style={{ gridTemplateColumns: `repeat(${rules.columns}, minmax(0,1fr))` }} aria-label="翻牌配对牌桌">
      {cards.map((card, index) => {
        const open = phase === 'preview' || phase === 'scan' || first === index || second === index || matched.has(index);
        const wrong = phase === 'feedback' && last?.outcome !== 'match' && (index === first || index === second);
        return <button key={card.id} className={`match-card ${open ? 'face-up' : ''} ${matched.has(index) ? 'paired' : ''} ${wrong ? 'wrong' : ''}`} disabled={phase !== 'play' || matched.has(index) || index === first} aria-label={`第 ${index + 1} 张，${matched.has(index) ? '已配对，' : ''}${open ? matchCardName(card) : '未翻开'}`} onClick={() => flip(index)}>
          <span className="match-position">{String(index + 1).padStart(2, '0')}</span>
          {open ? <MatchFace card={card} /> : <span className="match-card-back" aria-hidden="true"><span /><span /></span>}
          {matched.has(index) && <Check className="match-check" size={12} />}
        </button>;
      })}
    </div>
    <p className="precision-feedback match-feedback" role="status">{feedback}</p>
    {phase === 'preview' ? <button className="match-action" onClick={begin}><Play size={15} />记好了，开始配对</button> : rules.scans > 0 && <button className="match-action secondary" onClick={scan} disabled={phase !== 'play' || first !== null || scans >= rules.scans}><Eye size={15} />重看牌面<span>剩余 {rules.scans - scans} 次 · 扣 5 分</span></button>}
    <div className="match-progress" aria-label={`已配对 ${stats.matched} 对`}>{Array.from({ length: rules.pairs }, (_, i) => <span key={i} className={i < stats.matched ? 'done' : ''}>{i < stats.matched ? <Check size={10} /> : i + 1}</span>)}</div>
    <PrecisionStats stats={[{ label: '本局评分', value: stats.score }, { label: '配对准确率', value: attempts.length ? `${stats.accuracy}%` : '—' }, { label: '首次命中', value: `${stats.pristine} 对` }]} />
    <p className="precision-note"><Sparkles size={12} className="inline mr-1" />每次翻两张算一次尝试，超时也计一次。首次命中：两张都未在配对阶段翻开且未重看。位置不移动，不靠抢快加分。</p>
  </div>;
}
